(() => {
  const STORAGE_KEY = 'express:quote-draft:v1';
  const VERSION = 1;
  const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const MISSING_PARENT_SKU_MESSAGE = 'This product is missing a SKU. Please contact us before requesting a quote.';

  function normalizeQuantity(value, fallback = 1) {
    const quantity = Number(value);
    return Number.isFinite(quantity) ? Math.min(quantity, 100) : fallback;
  }

  function getDefaultCurrency() {
    return window.Shopify?.currency?.active || 'AUD';
  }

  function getStorage() {
    try {
      return window.localStorage;
    } catch (_error) {
      return null;
    }
  }

  function parseMoneyValue(value) {
    const parsed = Number.parseFloat(String(value ?? '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function parseOptionProduct(rawValue) {
    if (!rawValue) return null;
    if (typeof rawValue === 'object') return rawValue;

    try {
      return JSON.parse(rawValue);
    } catch (_error) {
      return null;
    }
  }

  function parseQuoteOptions(rawValue) {
    if (!rawValue) return null;
    if (typeof rawValue === 'object') return rawValue;

    try {
      return JSON.parse(rawValue);
    } catch (_error) {
      return null;
    }
  }

  function normalizeLineItemProperties(properties = {}) {
    return Object.entries(properties || {}).reduce((normalized, [key, value]) => {
      if (value === null || typeof value === 'undefined') return normalized;

      normalized[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return normalized;
    }, {});
  }

  function cloneJson(value) {
    if (!value || typeof value !== 'object') return null;

    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_error) {
      return null;
    }
  }

  function normalizeBuilderSnapshot(input) {
    const snapshot = parseOptionProduct(input);
    if (!snapshot || typeof snapshot !== 'object') return null;

    const config = cloneJson(snapshot.config);
    if (!config) return null;

    return {
      type: snapshot.type || 'product_builder',
      config,
      selections: cloneJson(snapshot.selections) || {},
    };
  }

  function stableStringify(value) {
    if (!value || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return JSON.stringify(value.map((entry) => JSON.parse(stableStringify(entry))));

    return JSON.stringify(Object.keys(value).sort().reduce((sorted, key) => {
      sorted[key] = value[key];
      return sorted;
    }, {}));
  }

  function buildLineKey(item) {
    return `${item.id}:${stableStringify(item.properties || {})}`;
  }

  function normalizeQuoteOption(input = {}) {
    const priceCents = Number(input.priceCents ?? input.price_delta_cents ?? input.price ?? 0);
    const image = input.image && typeof input.image === 'object'
      ? {
        url: input.image.url || input.image.src || '',
        alt: input.image.alt || input.image.altText || input.title || '',
      }
      : null;

    return {
      title: String(input.title || ''),
      image: image && image.url ? image : null,
      priceCents: Number.isFinite(priceCents) ? priceCents : 0,
      included: Boolean(input.included),
      priceLabel: input.priceLabel ? String(input.priceLabel) : '',
    };
  }

  function normalizeOptionProductComponent(component = {}) {
    const quantity = normalizeQuantity(component.quantity ?? 1);

    return {
      id: component.id || '',
      value: component.value || '',
      sku: component.sku || '',
      quantity,
    };
  }

  class QuoteDraft {
    constructor(options = {}) {
      this.storage = options.storage || getStorage();
      this.storageKey = options.storageKey || STORAGE_KEY;
      this.maxAgeMs = options.maxAgeMs || MAX_AGE_MS;
      this.now = options.now || (() => Date.now());
      this.memoryDraft = null;
    }

    getEmptyDraft(currency) {
      return {
        version: VERSION,
        currency: currency || getDefaultCurrency(),
        updatedAt: this.now(),
        items: [],
      };
    }

    readDraft() {
      if (!this.storage) return this.memoryDraft || this.getEmptyDraft();

      let parsed = null;
      try {
        parsed = JSON.parse(this.storage.getItem(this.storageKey) || 'null');
      } catch (_error) {
        return this.getEmptyDraft();
      }

      if (!parsed || parsed.version !== VERSION || !Array.isArray(parsed.items)) {
        return this.getEmptyDraft();
      }

      if (this.isExpired(parsed)) {
        this.clear();
        return this.getEmptyDraft(parsed.currency);
      }

      return Object.assign(this.getEmptyDraft(parsed.currency), parsed, {
        items: parsed.items.map((item) => this.normalizeItem(item)).filter((item) => item.id && item.quantity > 0),
      });
    }

    getDraft() {
      return this.readDraft();
    }

    isExpired(draft) {
      const updatedAt = Number(draft?.updatedAt || 0);
      return updatedAt > 0 && this.now() - updatedAt > this.maxAgeMs;
    }

    writeDraft(draft) {
      const nextDraft = Object.assign(this.getEmptyDraft(draft?.currency), draft || {}, {
        version: VERSION,
        updatedAt: this.now(),
        items: Array.isArray(draft?.items)
          ? draft.items.map((item) => this.normalizeItem(item)).filter((item) => item.id && item.quantity > 0)
          : [],
      });

      if (this.storage) {
        try {
          this.storage.setItem(this.storageKey, JSON.stringify(nextDraft));
        } catch (_error) {
          this.storage = null;
          this.memoryDraft = nextDraft;
        }
      } else {
        this.memoryDraft = nextDraft;
      }

      document.dispatchEvent(new CustomEvent('quote-draft:updated', { detail: { draft: nextDraft } }));
      return nextDraft;
    }

    clear() {
      if (this.storage) {
        try {
          this.storage.removeItem(this.storageKey);
        } catch (_error) {
          this.storage = null;
        }
      }

      this.memoryDraft = null;
      const draft = this.getEmptyDraft();
      document.dispatchEvent(new CustomEvent('quote-draft:updated', { detail: { draft } }));
      return draft;
    }

    normalizeItem(input = {}) {
      const quantity = normalizeQuantity(input.quantity ?? 1);

      const properties = normalizeLineItemProperties(input.properties || {});
      const unitPrice = parseMoneyValue(properties._price);
      const priceCents = Number(input.price ?? input.final_price ?? 0) || 0;
      const linePriceCents = Number(input.final_line_price ?? input.line_price ?? 0) || (
        unitPrice !== null ? Math.round(unitPrice * 100 * quantity) : priceCents * quantity
      );

      const normalizedItem = {
        id: input.id ? String(input.id) : '',
        variant_id: input.variant_id || input.id || '',
        product_id: input.product_id || input.productId || '',
        product_title: input.product_title || input.productTitle || input.title || '',
        title: input.title || input.product_title || input.productTitle || '',
        handle: input.handle || input.product_handle || '',
        url: input.url || input.product_url || '',
        image: input.image || null,
        quantity,
        sku: String(input.sku || '').trim(),
        price: priceCents,
        final_price: Number(input.final_price ?? input.price ?? priceCents) || 0,
        line_price: linePriceCents,
        final_line_price: linePriceCents,
        properties,
        builder: normalizeBuilderSnapshot(input.builder),
      };

      normalizedItem.line_key = input.line_key || input.key || buildLineKey(normalizedItem);
      return normalizedItem;
    }

    getLineKey(item) {
      return item?.line_key || buildLineKey(item || {});
    }

    recalculateLineTotals(item) {
      const quantity = Number(item.quantity) || 1;
      const unitPrice = parseMoneyValue(item.properties?._price);

      if (unitPrice !== null) {
        item.line_price = Math.round(unitPrice * 100 * quantity);
        item.final_line_price = item.line_price;
        return item;
      }

      const priceCents = Number(item.final_price ?? item.price ?? 0) || 0;
      item.line_price = priceCents * quantity;
      item.final_line_price = item.line_price;
      return item;
    }

    coalesceLineItems(items = []) {
      const itemMap = new Map();

      items.forEach((entry) => {
        if (!entry) return;

        const item = this.normalizeItem(entry);
        if (item.quantity <= 0) return;
        const lineKey = this.getLineKey(item);
        const existingItem = itemMap.get(lineKey);

        if (existingItem) {
          existingItem.quantity = normalizeQuantity(existingItem.quantity + item.quantity);
          this.recalculateLineTotals(existingItem);
          return;
        }

        itemMap.set(lineKey, item);
      });

      return Array.from(itemMap.values());
    }

    resolveItemIndex(draft, indexOrKey) {
      if (!draft || !Array.isArray(draft.items)) return -1;

      if (typeof indexOrKey === 'number' && Number.isInteger(indexOrKey)) {
        return indexOrKey >= 0 && indexOrKey < draft.items.length ? indexOrKey : -1;
      }

      const key = String(indexOrKey ?? '').trim();
      if (!key) return -1;

      if (/^\d+$/.test(key)) {
        const index = Number(key);
        if (index >= 0 && index < draft.items.length) return index;
      }

      return draft.items.findIndex((item) => this.getLineKey(item) === key || String(item.line_key || '') === key);
    }

    addItem(input = {}) {
      const item = this.normalizeItem(input);
      const draft = this.readDraft();
      const lineKey = this.getLineKey(item);
      const existingIndex = draft.items.findIndex((entry) => this.getLineKey(entry) === lineKey);

      if (item.quantity <= 0) {
        if (existingIndex === -1) return draft;
        draft.items.splice(existingIndex, 1);
        return this.writeDraft(draft);
      }
      if (!item.id) {
        throw new Error('Missing variant id for quote draft item.');
      }
      if (!String(item.sku || '').trim()) {
        throw new Error(MISSING_PARENT_SKU_MESSAGE);
      }

      const existingItem = draft.items[existingIndex];

      if (existingItem) {
        existingItem.quantity = normalizeQuantity(existingItem.quantity + item.quantity);
        this.recalculateLineTotals(existingItem);
      } else {
        draft.items.push(item);
      }

      return this.writeDraft(draft);
    }

    replaceItem(index = 0, input = {}) {
      const item = this.normalizeItem(input);
      if (item.quantity <= 0) return this.removeItem(index);
      if (!item.id) {
        throw new Error('Missing variant id for quote draft item.');
      }
      if (!String(item.sku || '').trim()) {
        throw new Error(MISSING_PARENT_SKU_MESSAGE);
      }

      item.line_key = buildLineKey(item);

      const draft = this.readDraft();
      const targetIndex = this.resolveItemIndex(draft, index);
      const items = draft.items.slice();
      items[targetIndex === -1 ? items.length : targetIndex] = item;
      draft.items = this.coalesceLineItems(items.filter(Boolean));

      return this.writeDraft(draft);
    }

    updateItemQuantity(indexOrKey, quantity) {
      const nextQuantity = normalizeQuantity(quantity, 0);

      const draft = this.readDraft();
      const targetIndex = this.resolveItemIndex(draft, indexOrKey);

      if (targetIndex === -1) return draft;
      if (nextQuantity <= 0) {
        return this.removeItem(indexOrKey);
      }

      draft.items[targetIndex].quantity = nextQuantity;
      this.recalculateLineTotals(draft.items[targetIndex]);
      return this.writeDraft(draft);
    }

    removeItem(indexOrKey) {
      const draft = this.readDraft();
      const targetIndex = this.resolveItemIndex(draft, indexOrKey);

      if (targetIndex === -1) return draft;

      draft.items.splice(targetIndex, 1);
      return this.writeDraft(draft);
    }

    buildQuoteProducts(items = []) {
      return items.flatMap((item) => {
        const quantity = normalizeQuantity(item.quantity, 0);
        if (quantity <= 0) return [];

        const products = [{
          id: item.id,
          title: item.title || item.product_title || '',
          quantity,
          sku: item.sku || '',
        }];

        Object.entries(item.properties || {}).forEach(([key, value]) => {
          if (!key.includes('option_product-')) return;

          const optionProduct = parseOptionProduct(value);
          if (!optionProduct) return;

          const hasComponents = Array.isArray(optionProduct.components);
          const components = hasComponents
            ? optionProduct.components
              .map(normalizeOptionProductComponent)
              .filter((component) => component.quantity > 0 && (component.id || component.sku))
            : [];

          if (hasComponents) {
            components.forEach((component) => {
              products.push({
                id: component.id,
                title: `${key.replace('option_product-', '')}: ${component.value || optionProduct.value || ''}`,
                quantity: normalizeQuantity(quantity * component.quantity),
                sku: component.sku || '',
              });
            });
            return;
          }

          products.push({
            id: optionProduct.id,
            title: `${key.replace('option_product-', '')}: ${optionProduct.value || ''}`,
            quantity,
            sku: optionProduct.sku || '',
          });
        });

        return products;
      });
    }

    buildQuoteOptions(item) {
      const metadata = parseQuoteOptions(item?.properties?._quote_options);
      if (metadata) {
        const rows = [];
        const optionRows = Array.isArray(metadata.items) ? metadata.items : [];
        optionRows.forEach((option) => {
          const row = normalizeQuoteOption(option);
          if (row.title) rows.push(row);
        });

        if (metadata.basePrice) {
          const basePrice = normalizeQuoteOption(metadata.basePrice);
          if (basePrice.title) rows.push(basePrice);
        }

        return rows;
      }

      return Object.entries(item?.properties || {}).flatMap(([key, value]) => {
        if (!key.includes('option_product-')) return [];

        const optionProduct = parseOptionProduct(value);
        if (!optionProduct) return [];

        return normalizeQuoteOption({
          title: `${key.replace('option_product-', '')}: ${optionProduct.value || ''}`,
          priceCents: 0,
          included: true,
        });
      });
    }

    getTotal(items = []) {
      return items.reduce((sum, item) => {
        const quantity = Number(item.quantity) || 0;
        const customUnitPrice = parseMoneyValue(item?.properties?._price);

        if (customUnitPrice !== null) {
          return sum + customUnitPrice * quantity;
        }

        const linePriceCents = Number(item.final_line_price ?? item.line_price ?? 0) || 0;
        return sum + linePriceCents / 100;
      }, 0);
    }
  }

  window.Express = window.Express || {};
  window.Express.missingParentSkuMessage = window.Express.missingParentSkuMessage || MISSING_PARENT_SKU_MESSAGE;
  window.Express.QuoteDraft = window.Express.QuoteDraft || QuoteDraft;
  window.Express.quoteDraft = window.Express.quoteDraft || new QuoteDraft();
})();
